import * as React from 'react';

import {Button} from 'shells/button/button';
import {ButtonGroup} from 'shells/button-group/button-group';
import {PageContentTwoColumn} from 'shells/layout/page-content-two-column';
import {Link} from 'shells/link/link';
import {Body} from 'shells/typography/body';
import {LabelWithContent} from 'shells/typography/label-with-content';

import {PageContainer, PageTitleBar} from '../../layout';

import {useGetAudiencesForPreferenceCenter, useMakeAudiencePrivate} from './graphql/hooks';
import {NoAudiencesEmptyState} from './no-audiences-empty-state';
import {PreferenceCenterPreview} from './preference-center-preview';
import {PublicAudienceModal} from './public-audience-modal';
import {PublicAudiencesList} from './public-audiences-list';

export function PreferenceCenterPage() {
    const [isAddingAudience, setIsAddingAudience] = React.useState(false);
    const [isEditingAudience, setIsEditingAudience] = React.useState<string | null | undefined>(
        null
    );

    const {audiences, isLoading} = useGetAudiencesForPreferenceCenter('NAME_ASC');
    const {makeAudiencePrivate} = useMakeAudiencePrivate();

    return (
        <PageContainer
            title='Email preference center'
            whiteBackground={true}
            pageTitleBar={
                <PageTitleBar
                    title='Email preference center'
                    subheaderPrimaryText='Let your recipients subscribe to audiences themselves'
                    buttons={
                        <ButtonGroup>
                            <Link
                                size='big'
                                newTab={true}
                                variant='button-secondary'
                                href='/preferences/preview'
                            >
                                View page preview
                            </Link>
                            <Button
                                size='big'
                                variant='primary'
                                onClick={() => setIsAddingAudience(true)}
                                disabled={isLoading || audiences.length >= 10}
                            >
                                Add preference
                            </Button>
                        </ButtonGroup>
                    }
                />
            }
        >
            <PageContentTwoColumn
                overflowVisible={true}
                withoutOuterPadding={true}
                leftContent={
                    <LabelWithContent
                        label='Preview'
                        isFullHeight={true}
                        content={
                            <PreferenceCenterPreview audiences={audiences} isLoading={isLoading} />
                        }
                    />
                }
                rightContent={
                    <LabelWithContent
                        label='Audiences on the preference center'
                        content={
                            <div className='flex flex-dir-col gap-8'>
                                <Body color='light' size='small'>
                                    Create or promote existing audiences to the email preference
                                    center in order to have them appear on this page
                                </Body>
                                {audiences.length === 0 && !isLoading ? (
                                    <NoAudiencesEmptyState
                                        onAddAudience={() => setIsAddingAudience(true)}
                                    />
                                ) : (
                                    <PublicAudiencesList
                                        audiences={audiences}
                                        isLoading={isLoading}
                                        onAddAudience={() => setIsAddingAudience(true)}
                                        onEditAudience={(audience) => {
                                            setIsEditingAudience(audience.id);
                                        }}
                                        onRemoveAudience={(audience) => {
                                            makeAudiencePrivate(audience.id);
                                        }}
                                    />
                                )}
                            </div>
                        }
                    />
                }
            />
            {(isAddingAudience || Boolean(isEditingAudience)) && (
                <PublicAudienceModal
                    isOpen={isAddingAudience || Boolean(isEditingAudience)}
                    onClose={() => {
                        setIsAddingAudience(false);
                        setIsEditingAudience(null);
                    }}
                    audienceId={isEditingAudience}
                />
            )}
        </PageContainer>
    );
}
